// src/components/pickers/ClientPickerModal.jsx
import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Alert,
  Row,
  Col,
  InputGroup,
} from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import api from "../../axiosConfig";

async function authGetJson(url) {
  const r = await authFetch(url);

  // deja JSON (în cazul în care authFetch întoarce direct obiect)
  if (r && typeof r === "object" && !("ok" in r) && !("json" in r)) return r;

  // Response
  if (r?.ok) return await r.json();

  const msg = r?.text ? await r.text() : "Request failed";
  throw new Error(msg);
}

export default function ClientPickerModal({
  show,
  onHide,
  onPick,
  initialQuery = "",
  minChars = 0, // pune 2 dacă vrei doar după 2 litere
  title = "Alege contact",
}) {
  const [view, setView] = useState("list"); // "list" sau "create"
  const [q, setQ] = useState(initialQuery);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [page, setPage] = useState(1);

  const loadingPageRef = useRef(null);
  const lastFetchedQueryRef = useRef(null);

  // State-uri pentru adăugare contact nou
  const [judetOptions, setJudetOptions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [createErr, setCreateErr] = useState("");
  const [anafLoading, setAnafLoading] = useState(false);
  const [anafError, setAnafError] = useState("");
  const [createErrors, setCreateErrors] = useState({});
  const [createForm, setCreateForm] = useState({
    bxcodfiscal: "", // CUI
    bxdenumire: "",
    bxadresa: "",
    bxjudet_id: "",
    judet_str: "",
    bxoras: "",
    bxcodpostal: "",
    bxtara: "România",
    bxemail: "",
    bxtel: "",
    bxregcom: "",
    bxbanca_nume: "",
    bxbanca_cont: "",
    bxseria: "",
    bxfax: "",
    bxinout_bool: 1, // 1 = juridică, 2 = fizică
    bxcatalog_id: "",
    bxsector_id: "",
  });

  const limit = 20;

  // Încărcare județe la montare
  useEffect(() => {
    api
      .get("/judet/", { params: { limit: 100 } })
      .then((res) => setJudetOptions(res.data?.items ?? []))
      .catch(() => setJudetOptions([]));
  }, []);

  const unaccent = (s = "") =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  const fetchPage = async (nextPage = 1, query = q) => {
    const qq = (query || "").trim();

    if (qq.length < minChars) {
      setItems([]);
      setTotal(null);
      setPage(1);
      setErr("");
      return;
    }

    if (loadingPageRef.current === nextPage) {
      return;
    }
    loadingPageRef.current = nextPage;

    setBusy(true);
    setErr("");

    try {
      const params = new URLSearchParams({
        skip: String((nextPage - 1) * limit),
        limit: String(limit),
        ...(qq ? { search: qq } : {}),
      });

      const url = `/contacte/?${params.toString()}`;
      const data = await authGetJson(url);

      const list = data?.items || data || [];
      const t = data?.total ?? null;

      const mapped = list.map((it) => {
        const id = it.id ?? it.bxid ?? it.contact_id;

        const code =
          it.bxcodfiscal ??
          it.cod ??
          it.bxcod ??
          it.code ??
          it.cui ??
          it.cod_fiscal ??
          "";

        const name =
          it.denumire ??
          it.bxdenumire ??
          it.name ??
          it.nume ??
          it.denumire_client ??
          "";

        const label = `${code ? code + " • " : ""}${name || "#" + id}`;

        return { id, label, raw: it, code, name };
      });

      setItems((prev) => (nextPage === 1 ? mapped : [...prev, ...mapped]));
      setTotal(t);
      setPage(nextPage);
      lastFetchedQueryRef.current = qq;
    } catch (e) {
      setErr(String(e?.message || e));
      if (nextPage === 1) {
        setItems([]);
        setTotal(null);
      }
    } finally {
      setBusy(false);
      loadingPageRef.current = null;
    }
  };

  // Reset form adăugare contact
  const resetCreateForm = () => {
    setCreateForm({
      bxcodfiscal: "",
      bxdenumire: "",
      bxadresa: "",
      bxjudet_id: "",
      judet_str: "",
      bxoras: "",
      bxcodpostal: "",
      bxtara: "România",
      bxemail: "",
      bxtel: "",
      bxregcom: "",
      bxbanca_nume: "",
      bxbanca_cont: "",
      bxseria: "",
      bxfax: "",
      bxinout_bool: 1,
      bxcatalog_id: "",
      bxsector_id: "",
    });
    setCreateErrors({});
    setAnafError("");
    setCreateErr("");
  };

  // Inițializare modal
  useEffect(() => {
    if (!show) return;

    setView("list");
    resetCreateForm();
    const initQ = initialQuery || "";
    setQ(initQ);
    setItems([]);
    setTotal(null);
    setPage(1);
    setErr("");
    lastFetchedQueryRef.current = null;

    if (initQ.trim().length >= minChars) {
      fetchPage(1, initQ);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Căutare automată la tastare (debounce)
  useEffect(() => {
    if (!show || view !== "list") return;

    const query = q.trim();
    if (query.length < minChars) {
      setItems([]);
      setTotal(null);
      setPage(1);
      return;
    }

    if (lastFetchedQueryRef.current === query) {
      return;
    }

    const t = setTimeout(() => {
      fetchPage(1, q);
    }, 300);

    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, show, view]);

  const hasMore = total != null ? page * limit < total : items.length === limit;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 15) {
      if (!busy && hasMore) {
        fetchPage(page + 1, q);
      }
    }
  };

  const pick = (opt) => {
    if (!opt) return;

    // label consistent "cod • denumire"
    const code =
      opt.raw?.bxcodfiscal ??
      opt.raw?.cod ??
      opt.raw?.bxcod ??
      opt.raw?.code ??
      opt.raw?.cui ??
      opt.raw?.cod_fiscal ??
      "";

    const name =
      opt.raw?.denumire ??
      opt.raw?.bxdenumire ??
      opt.raw?.name ??
      opt.raw?.nume ??
      "";

    const label = `${code ? code + " • " : ""}${name || ""}`.trim();

    onPick?.({
      id: opt.id,
      label,
      raw: opt.raw,
    });

    onHide?.();
  };

  // Handlers pentru crearea contactului
  const handleCreateChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCreateForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (createErrors[name])
      setCreateErrors((er) => ({ ...er, [name]: undefined }));
  };

  const handleJudetChange = (e) => {
    const id = e.target.value;
    const opt = judetOptions.find((o) => String(o.id) === String(id));
    setCreateForm((f) => ({
      ...f,
      bxjudet_id: id,
      judet_str: opt?.denumire || "",
    }));
    if (createErrors.bxjudet_id)
      setCreateErrors((er) => ({ ...er, bxjudet_id: undefined }));
  };

  const fetchFromANAF = async () => {
    setAnafError("");
    const cuiRaw = (createForm.bxcodfiscal || "").toUpperCase().trim();
    const cuiDigits = cuiRaw.replace(/[^0-9]/g, "");
    if (!cuiDigits) {
      setCreateErrors((er) => ({ ...er, bxcodfiscal: "Introduceți CUI." }));
      return;
    }

    try {
      setAnafLoading(true);
      const { data } = await api.post("/anaf/lookup", { cui: cuiDigits });

      setCreateForm((f) => {
        const next = { ...f };

        const tvaYes =
          data.inregistrare_scop_Tva === "1" || data.scpTVA === true;
        next.bxcodfiscal = tvaYes ? `RO${data.cui}` : String(data.cui);

        if (data.denumire) next.bxdenumire = data.denumire;
        if (data.adresa) next.bxadresa = data.adresa;
        if (data.nrRegCom) next.bxregcom = data.nrRegCom;
        if (data.telefon) next.bxtel = data.telefon;
        if (data.oras) next.bxoras = data.oras;
        if (data.codPostal) next.bxcodpostal = data.codPostal;
        next.bxtara = "România";

        // Deducere județ
        if (data.judet || data.adresa) {
          const addr = unaccent(`${data.judet || ""} ${data.adresa || ""}`);
          let match = judetOptions.find(
            (o) =>
              unaccent(o.denumire || "") && addr.includes(unaccent(o.denumire)),
          );
          if (!match && addr.includes("bucuresti")) {
            match = judetOptions.find((o) =>
              unaccent(o.denumire || "").includes("bucuresti"),
            );
          }
          if (match) {
            next.bxjudet_id = String(match.id);
            next.judet_str = match.denumire || "";
          }
        }

        return next;
      });
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setAnafError(`Eroare preluare date ANAF: ${msg}`);
    } finally {
      setAnafLoading(false);
    }
  };

  const validateCreate = () => {
    const er = {};
    if (!createForm.bxdenumire.trim())
      er.bxdenumire = "Denumirea este obligatorie.";
    if (!createForm.bxcodfiscal.trim())
      er.bxcodfiscal = "CUI este obligatoriu.";
    if (!createForm.bxadresa.trim()) er.bxadresa = "Adresa este obligatorie.";
    if (!String(createForm.bxjudet_id)) er.bxjudet_id = "Alegeți județul.";
    setCreateErrors(er);
    return Object.keys(er).length === 0;
  };

  const toIntOrUndef = (v) => (v === "" || v == null ? undefined : Number(v));

  const cleanPayload = (obj) => {
    const out = {};
    for (const [k, v] of Object.entries(obj))
      if (v !== "" && v != null) out[k] = v;

    ["bxcatalog_id", "bxsector_id", "bxjudet_id"].forEach((k) => {
      if (k in out) out[k] = toIntOrUndef(out[k]);
      if (out[k] === undefined) delete out[k];
    });

    out.bxinout_bool = [1, 2].includes(Number(out.bxinout_bool))
      ? Number(out.bxinout_bool)
      : 1;
    delete out.e_portal;

    return out;
  };

  const handleSaveContact = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!validateCreate()) return;

    setSubmitting(true);
    setCreateErr("");
    const payload = cleanPayload(createForm);

    try {
      const { data } = await api.post("/contacte/", payload);

      // Selectare automată client creat
      const code =
        data.cod ??
        data.bxcod ??
        data.cui ??
        data.cod_fiscal ??
        data.bxcodfiscal ??
        "";
      const name =
        data.denumire ?? data.name ?? data.nume ?? data.bxdenumire ?? "";
      const label = `${code ? code + " • " : ""}${name || "#" + data.id}`;

      onPick?.({
        id: data.id,
        label,
        raw: data,
      });

      setView("list");
      resetCreateForm();
      onHide?.();
    } catch (err) {
      const detail = err?.response?.data;
      let msg = err?.message || "Eroare necunoscută";

      if (Array.isArray(detail)) {
        msg = detail
          .map((e) => `${(e.loc || []).join(".")}: ${e.msg}`)
          .join("\n");
      } else if (detail?.detail) {
        msg =
          typeof detail.detail === "string"
            ? detail.detail
            : JSON.stringify(detail.detail);
      }
      setCreateErr("Eroare la creare:\n" + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {view === "create" ? "Adaugă Firmă Nouă" : title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {view === "list" ? (
          <>
            {err && <Alert variant="danger">{err}</Alert>}

            <div className="d-flex gap-2">
              <Form.Control
                size="sm"
                style={{ fontSize: "15px", height: "35px" }}
                autoFocus
                autoComplete="off"
                placeholder={
                  minChars >= 2
                    ? `Caută contact... (minim ${minChars} caractere)`
                    : "Caută contact..."
                }
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.stopPropagation();
                    fetchPage(1, q);
                  }
                }}
              />

              <Button
                type="button"
                variant="secondary"
                size="sm"
                style={{
                  fontSize: "15px",
                  height: "35px",
                  whiteSpace: "nowrap",
                }}
                disabled={busy}
                onClick={() => fetchPage(1, q)}
              >
                Refresh
              </Button>
              <Button
                type="button"
                variant="success"
                size="sm"
                style={{
                  fontSize: "15px",
                  height: "35px",
                  whiteSpace: "nowrap",
                }}
                onClick={() => setView("create")}
              >
                + Firmă Nouă
              </Button>
            </div>

            <div className="mt-3">
              {busy && page === 1 && items.length === 0 ? (
                <div className="d-flex align-items-center gap-2">
                  <Spinner size="sm" />
                  <span>Se încarcă...</span>
                </div>
              ) : (
                <>
                  <div
                    style={{ maxHeight: "400px", overflowY: "auto" }}
                    onScroll={handleScroll}
                  >
                    <Table hover size="sm" className="mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: 80 }}>ID</th>
                          <th style={{ width: 150 }}>CUI / CIF</th>
                          <th>Denumire</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((it) => (
                          <tr
                            key={String(it.id)}
                            role="button"
                            onClick={() => pick(it)}
                            style={{ cursor: "pointer" }}
                          >
                            <td style={{ width: 80, whiteSpace: "nowrap" }}>
                              #{it.id}
                            </td>
                            <td style={{ width: 150, whiteSpace: "nowrap" }}>
                              {it.code}
                            </td>
                            <td>{it.name}</td>
                          </tr>
                        ))}

                        {items.length === 0 && !busy && (
                          <tr>
                            <td colSpan={3} className="text-muted">
                              {(q || "").trim().length < minChars
                                ? `Tastează cel puțin ${minChars} caractere pentru căutare automată.`
                                : "Nimic găsit."}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {busy && page > 1 && (
                    <div className="d-flex align-items-center gap-2 mt-2 justify-content-center">
                      <Spinner size="sm" />
                      <span className="text-muted" style={{ fontSize: 12 }}>
                        Se încarcă mai multe...
                      </span>
                    </div>
                  )}

                  <div className="d-flex justify-content-between align-items-center mt-2">
                    <div className="text-muted" style={{ fontSize: 12 }}>
                      {total != null ? `Total: ${total}` : ""}
                    </div>
                  </div>
                </>
              )}
            </div>
          </>
        ) : (
          <Form onSubmit={handleSaveContact} noValidate>
            {createErr && (
              <Alert variant="danger" style={{ whiteSpace: "pre-line" }}>
                {createErr}
              </Alert>
            )}

            <Row className="g-3">
              {/* 1) CUI + Ia date ANAF */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    CUI <span className="text-danger">*</span>
                  </Form.Label>
                  <InputGroup>
                    <Form.Control
                      name="bxcodfiscal"
                      value={createForm.bxcodfiscal}
                      onChange={handleCreateChange}
                      placeholder="RO1234567 sau 1234567"
                      isInvalid={!!createErrors.bxcodfiscal}
                    />
                    <Button
                      variant="outline-primary"
                      onClick={fetchFromANAF}
                      disabled={anafLoading}
                    >
                      {anafLoading ? (
                        <>
                          <Spinner
                            size="sm"
                            animation="border"
                            className="me-1"
                          />
                          Se caută…
                        </>
                      ) : (
                        "Ia date ANAF"
                      )}
                    </Button>
                    <Form.Control.Feedback type="invalid">
                      {createErrors.bxcodfiscal}
                    </Form.Control.Feedback>
                  </InputGroup>
                  {anafError && (
                    <div className="small text-danger mt-1">{anafError}</div>
                  )}
                </Form.Group>
              </Col>

              {/* 2) Denumire (obligatoriu) */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Denumire <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="bxdenumire"
                    value={createForm.bxdenumire}
                    onChange={handleCreateChange}
                    isInvalid={!!createErrors.bxdenumire}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createErrors.bxdenumire}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* 3) Adresă (obligatoriu) */}
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>
                    Adresă <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    name="bxadresa"
                    value={createForm.bxadresa}
                    onChange={handleCreateChange}
                    isInvalid={!!createErrors.bxadresa}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createErrors.bxadresa}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              {/* 4) Județ (obligatoriu) */}
              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>
                    Județ <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="bxjudet_id"
                    value={
                      createForm.bxjudet_id ? String(createForm.bxjudet_id) : ""
                    }
                    onChange={handleJudetChange}
                    isInvalid={!!createErrors.bxjudet_id}
                  >
                    <option value="">— alege —</option>
                    {judetOptions.map((opt) => (
                      <option key={opt.id} value={String(opt.id)}>
                        {opt.denumire ?? opt.cod ?? opt.id}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {createErrors.bxjudet_id}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Oraș</Form.Label>
                  <Form.Control
                    name="bxoras"
                    value={createForm.bxoras}
                    onChange={handleCreateChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="bxemail"
                    value={createForm.bxemail}
                    onChange={handleCreateChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Telefon</Form.Label>
                  <Form.Control
                    name="bxtel"
                    value={createForm.bxtel}
                    onChange={handleCreateChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Reg. Com.</Form.Label>
                  <Form.Control
                    name="bxregcom"
                    value={createForm.bxregcom}
                    onChange={handleCreateChange}
                    placeholder="ex: J40/123/2020"
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>Bancă</Form.Label>
                  <Form.Control
                    name="bxbanca_nume"
                    value={createForm.bxbanca_nume}
                    onChange={handleCreateChange}
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={4}>
                <Form.Group>
                  <Form.Label>IBAN</Form.Label>
                  <Form.Control
                    name="bxbanca_cont"
                    value={createForm.bxbanca_cont}
                    onChange={handleCreateChange}
                    placeholder="RO..."
                  />
                </Form.Group>
              </Col>

              <Col xs={12} md={6}>
                <Form.Group>
                  <Form.Label>Tip persoană</Form.Label>
                  <Form.Select
                    name="bxinout_bool"
                    value={createForm.bxinout_bool}
                    onChange={(e) =>
                      setCreateForm((f) => ({
                        ...f,
                        bxinout_bool: Number(e.target.value),
                      }))
                    }
                  >
                    <option value={1}>1 — Persoană juridică</option>
                    <option value={2}>2 — Persoană fizică</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setView("list");
                  resetCreateForm();
                }}
                disabled={submitting}
              >
                Renunță
              </Button>
              <Button type="submit" variant="dark" disabled={submitting}>
                {submitting ? "Se salvează…" : "Salvează"}
              </Button>
            </div>
          </Form>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
